import React from 'react';
import { styled } from '../../../../shared/Theme/theme';
import { businessPartner, settings } from '../../../../../models/entities/businessPartner/businessPartner';
import { ePaymentTerms } from '../../../../../models/entities/businessPartner/paymentTerms';
import { userInfo } from '../../../../../models/entities/user';
import { InfoCircleOutlined } from '@ant-design/icons';
import { formatMoney } from './Accounting/format';

type Props = {
    businessPartner: businessPartner;
    settings?: settings;
    users: userInfo[];
    actions?: React.ReactNode;
};

const TYPE_PILL_STYLES: Record<string, { bg: string; color: string; label: string }> = {
    CARGO_OWNER: { bg: '#e8f4fc', color: '#1f7ab8', label: 'Cargo Owner' },
    FORWARDER_BROKER: { bg: '#252b4a', color: '#ffffff', label: 'Forwarder / Broker' },
    SUPPLIER: { bg: '#252b4a', color: '#ffffff', label: 'Supplier' },
    MANUFACTURER: { bg: '#252b4a', color: '#ffffff', label: 'Manufacturer' },
    CUSTOMER: { bg: '#252b4a', color: '#ffffff', label: 'Customer' },
    WAREHOUSE: { bg: '#252b4a', color: '#ffffff', label: 'Warehouse' }
};

const initials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const usernamesFromIds = (csv: string | undefined, users: userInfo[]): string => {
    if (!csv) return '';
    const ids = csv.split(',').map((s) => s.trim()).filter(Boolean);
    return users
        .filter((u) => ids.includes(u.id))
        .map((u) => `${u.firstName} ${u.lastName}`)
        .join(', ');
};

const PersonField = ({ name }: { name: string }) => {
    if (!name) return <FieldValue>-</FieldValue>;
    return (
        <PersonRow>
            <Avatar>{initials(name)}</Avatar>
            <span>{name}</span>
        </PersonRow>
    );
};

const CompactHeader = ({ businessPartner: bp, settings, users, actions }: Props) => {
    const partyType = settings?.partyTypes?.find((t) => t.code === bp.type);
    const typeKey = (bp.type || '').toUpperCase();
    const typeStyle = TYPE_PILL_STYLES[typeKey] ?? { bg: '#eef2f7', color: '#475467', label: partyType?.name || bp.type };
    const creditLineDisplay =
        bp.creditLine && Number(bp.creditLine) > 0 ? formatMoney(Number(bp.creditLine), bp.creditLineCurrency, { decimals: 'auto' }) : '-';
    const paymentTermsLabel = bp.paymentTerms ? ePaymentTerms[bp.paymentTerms] || bp.paymentTerms : '-';

    return (
        <Card>
            <TopRow>
                <TitleBlock>
                    <Title>
                        {bp.name}
                        <BpNumber>#{bp.BusinessPartnerNumber}</BpNumber>
                    </Title>
                    <TypePill style={{ background: typeStyle.bg, color: typeStyle.color }}>{typeStyle.label}</TypePill>
                </TitleBlock>
                <Actions>{actions}</Actions>
            </TopRow>

            <FieldsRow>
                <Field>
                    <FieldTitle>Phone</FieldTitle>
                    <FieldValue>{bp.phoneNumber || '-'}</FieldValue>
                </Field>
                <Field>
                    <FieldTitle>Email</FieldTitle>
                    <FieldValue title={bp.email || ''}>{bp.email || '-'}</FieldValue>
                </Field>
                <Field>
                    <FieldTitle>VAT</FieldTitle>
                    <FieldValue>{bp.vatNumber || '-'}</FieldValue>
                </Field>
                <Field>
                    <FieldTitle>Credit Line</FieldTitle>
                    <FieldValue>{creditLineDisplay}</FieldValue>
                </Field>
                <Field>
                    <FieldTitle>Payment Terms</FieldTitle>
                    <FieldValue>
                        {paymentTermsLabel}{' '}
                        {bp.paymentTermsRemarks ? (
                            <InfoIcon title={bp.paymentTermsRemarks}>
                                <InfoCircleOutlined />
                            </InfoIcon>
                        ) : null}
                    </FieldValue>
                </Field>
                <Field>
                    <FieldTitle>Salesman</FieldTitle>
                    <PersonField name={usernamesFromIds(bp.salesman, users)} />
                </Field>
                <Field>
                    <FieldTitle>Operations Manager</FieldTitle>
                    <PersonField name={usernamesFromIds(bp.operationsManager, users)} />
                </Field>
            </FieldsRow>
        </Card>
    );
};

const Card = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04);
    padding: 18px 22px 16px;
    margin: 0 18px 14px 18px;
`;

const TopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
`;

const TitleBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const Title = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: ${(p) => p.theme.colors.pageTitle};
    line-height: 24px;
`;

const BpNumber = styled.span`
    color: #94a3b8;
    font-weight: 500;
    margin-left: 6px;
`;

const TypePill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const FieldsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 18px;
    @media (max-width: 1280px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        row-gap: 16px;
    }
    @media (max-width: 768px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const FieldTitle = styled.div`
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
`;

const FieldValue = styled.div`
    font-size: 13px;
    color: #1f2937;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const InfoIcon = styled.span`
    color: #94a3b8;
    margin-left: 4px;
    font-size: 12px;
    cursor: help;
`;

const PersonRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #1f2937;
    font-size: 13px;
    overflow: hidden;
    & > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const Avatar = styled.span`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, #75b9e0 0%, #4a8ec2 100%);
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export default CompactHeader;
